import React, { FormEvent } from "react";
import { Package, ShoppingCart, Box, FileText, Send, AlertTriangle, AlertCircle } from "lucide-react";
import { PurchaseOrder } from "src/types";
import { useScrollOnMessage } from "src/hooks";
import { ParentPOProductsTable } from "src/components/tables";
import { FormLvlErrorMessage } from "src/components/ui/feedback";
import { DD_MM_YYYY } from "src/helpers";

interface ConfirmChildPurchaseOrderFormProps {
  childPurchaseOrder: PurchaseOrder;
  formLvlErrorMessage: string;
  isSubmitting: boolean;
  handleSubmit: (e?: FormEvent) => void | Promise<void>;
}

const ConfirmChildPurchaseOrderForm: React.FC<ConfirmChildPurchaseOrderFormProps> = ({
  childPurchaseOrder,
  formLvlErrorMessage,
  isSubmitting,
  handleSubmit
}) => {
  const formLvlErrorMessageRef = useScrollOnMessage(formLvlErrorMessage);

  const opsNote = childPurchaseOrder.additionalInfo?.operationsNote?.body;
  const estDelivery =
    childPurchaseOrder.confirmationDetails?.estDeliveryDate ?? childPurchaseOrder.estimatedDeliveryDate;

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="mx-auto">
        <div className="bg-white rounded-2xl shadow-soft overflow-hidden">
          {/* Header Section */}
          <div className="bg-black px-8 py-6">
            <h1 className="text-3xl font-bold text-white tracking-tight">Confirm Child Purchase Order</h1>
            <p className="text-gray-300 mt-2 text-lg">
              Review the auto-created overage child PO and confirm to send the ASN to GXO
            </p>
          </div>

          <form onSubmit={handleSubmit} autoComplete="off" className="p-8">
            <div className="space-y-6">
              {/* Child PO Info */}
              <div className="border border-primary-200 rounded-2xl p-6 bg-primary-50">
                <h3 className="text-sm font-semibold text-primary-900 mb-4 tracking-wide uppercase flex items-center">
                  <ShoppingCart className="w-4 h-4 mr-2" />
                  Child Purchase Order Details
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <span className="block text-xs font-medium text-primary-700 mb-1">Child PO ID</span>
                    <span className="block text-sm font-semibold text-primary-900">{childPurchaseOrder.id}</span>
                  </div>
                  <div>
                    <span className="block text-xs font-medium text-primary-700 mb-1">Parent PO</span>
                    <span className="block text-sm font-semibold text-primary-900">
                      {childPurchaseOrder.parentPurchaseOrderId ?? "—"}
                    </span>
                  </div>
                  <div>
                    <span className="block text-xs font-medium text-primary-700 mb-1">Vendor</span>
                    <span className="block text-sm font-semibold text-primary-900">
                      {childPurchaseOrder.vendorName ?? "—"}
                    </span>
                  </div>
                  <div>
                    <span className="block text-xs font-medium text-primary-700 mb-1">Checked In By</span>
                    <span className="block text-sm font-semibold text-primary-900">
                      {childPurchaseOrder.confirmationDetails?.confirmedByEmail ?? "—"}
                    </span>
                  </div>
                  <div>
                    <span className="block text-xs font-medium text-primary-700 mb-1">Est Delivery</span>
                    <span className="block text-sm font-semibold text-primary-900">
                      {estDelivery ? DD_MM_YYYY.formatDate(new Date(estDelivery)) : "—"}
                    </span>
                  </div>
                  <div>
                    <span className="block text-xs font-medium text-primary-700 mb-1">Status</span>
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-warning-100 text-warning-800">
                      Pending Confirmation
                    </span>
                  </div>
                </div>
              </div>

              {/* Products Table */}
              <div className="border border-neutral-200 rounded-2xl p-6 bg-white">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-semibold text-black tracking-wide uppercase flex items-center">
                    <Box className="w-4 h-4 mr-2" />
                    Child PO Products
                  </h3>
                  <span className="text-xs font-medium text-neutral-500 bg-neutral-100 px-3 py-1 rounded-full">
                    {childPurchaseOrder.products?.length || 0} Products
                  </span>
                </div>
                {childPurchaseOrder.products && childPurchaseOrder.products.length > 0 ? (
                  <ParentPOProductsTable products={childPurchaseOrder.products} />
                ) : (
                  <div className="bg-neutral-50 border border-dashed border-neutral-300 rounded-lg p-8 text-center">
                    <Package className="w-12 h-12 mx-auto mb-3 text-neutral-400" />
                    <p className="text-sm text-neutral-600">No products on this child PO</p>
                  </div>
                )}
              </div>

              {/* Operations Note */}
              {opsNote && (
                <div className="border border-neutral-200 rounded-2xl p-5 bg-white">
                  <h3 className="text-sm font-semibold text-black mb-3 tracking-wide uppercase flex items-center">
                    <FileText className="w-4 h-4 mr-2" />
                    Operations Note
                  </h3>
                  <p className="text-sm text-secondary-700 whitespace-pre-wrap">{opsNote}</p>
                </div>
              )}

              {/* Form Level Error (includes GXO error message) */}
              <FormLvlErrorMessage message={formLvlErrorMessage} centred={false} ref={formLvlErrorMessageRef} />

              {/* Submit Button */}
              <div className="flex items-center gap-4">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-6 py-3 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors text-sm font-medium disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center space-x-2"
                >
                  <Send className="w-4 h-4" />
                  <span>{isSubmitting ? "Confirming…" : "Confirm & Send to GXO"}</span>
                </button>
                <div className="flex items-center space-x-2 text-sm text-gray-600">
                  <AlertCircle className="w-4 h-4 text-primary-500" />
                  <span>The ASN will be sent to GXO and the PO will move to In Progress</span>
                </div>
              </div>

              {/* Info Banner */}
              <div className="border border-warning-200 rounded-2xl p-5 bg-warning-50">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-warning-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-semibold text-warning-800 mb-1">Awaiting confirmation</p>
                    <p className="text-sm text-warning-700">
                      This child PO was auto-created from an overage during warehouse check-in. Once confirmed, the ASN
                      is sent to GXO and the PO becomes <span className="font-semibold">In Progress</span>.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export { ConfirmChildPurchaseOrderForm };
